import { Theme } from "@material-ui/core/styles";

const cache = new Map();

const calculateNormalizedBrandPaletteValues = (
  theme: Theme
): Record<string, string | undefined> => {
  const isCash = typeof theme.cash !== "undefined";

  const { black, white } = theme.palette.common;
  const formBorder = isCash
    ? `1px solid ${theme.cash.palette.gray80}`
    : undefined;

  const palette: Record<string, string | undefined> = {
    // mapping colors to the theme
    blue: isCash ? theme.cash.palette.blue : theme.bronte.palette.orca,
    gray10: isCash ? theme.cash.palette.gray10 : theme.bronte.palette.gray10,
    gray20: isCash ? theme.cash.palette.gray10 : theme.bronte.palette.gray20,
    gray30: isCash ? theme.cash.palette.gray40 : theme.bronte.palette.gray30,
    gray40: isCash ? theme.cash.palette.gray40 : theme.bronte.palette.gray40,
    gray60: isCash ? theme.cash.palette.gray60 : theme.bronte.palette.gray60,
    gray80: isCash ? theme.cash.palette.gray60 : theme.bronte.palette.gray80,
    gray90: isCash ? theme.cash.palette.gray90 : theme.bronte.palette.gray90,
    gray96: isCash ? theme.cash.palette.gray96 : theme.bronte.palette.gray96,
    successBg: isCash ? theme.cash.palette.green : theme.bronte.palette.green,
    successFg: isCash ? black : theme.bronte.palette.green50,
    error: isCash ? theme.cash.palette.red : theme.bronte.palette.red,

    headerOnDark: isCash
      ? theme.cash.palette.green
      : theme.bronte.palette.bondiMint,

    // component specific colors
    connectorContainer: isCash ? white : theme.bronte.palette.orcaPastel,
    applyStepSuccess: isCash
      ? theme.cash.palette.green
      : theme.bronte.palette.bondiMint,
    faqFabBg: isCash ? black : "#1d4371",
    // progress stepper colors
    progressBar: isCash
      ? theme.cash.palette.gray60
      : theme.bronte.palette.gray80,
    progressBarActive: isCash ? theme.cash.palette.gray10 : black,
    stepCircleNotStartedBg: isCash ? theme.cash.palette.gray60 : white,
    stepCircleNotStartedBorder: isCash ? theme.cash.palette.gray60 : black,
    stepCircleInProgressBg: isCash ? white : black,
    stepCircleInProgressBorder: isCash ? theme.cash.palette.gray10 : black,

    dividerHr: isCash ? theme.cash.palette.gray60 : theme.bronte.palette.plum,
    valuePropsBg: isCash ? theme.cash.palette.gray97 : black,
    valuePropsFg: isCash ? theme.cash.palette.gray10 : white,
    secondValuePropOffset: isCash ? undefined : `translateX(-8px)`,

    accordionDetailsSubtitleFg: isCash
      ? theme.cash.palette.gray10
      : theme.bronte.palette.gray30,

    accordianHeaderBorder: formBorder,
    accordianHeaderFlex: isCash ? "1 1 auto" : undefined,

    radioLabelWidth: isCash ? "100%" : "70%",
    radioWrapperDisplay: isCash ? "block" : "flex",
    radiosWrapperMarginLeft: isCash ? "4px" : undefined,
    radioHeaderColor: isCash
      ? theme.cash.palette.gray10
      : theme.bronte.palette.gray40,

    // form footer colors
    formFooterBg: isCash ? undefined : theme.bronte.palette.gray96,
    formFooterBorder: formBorder,

    // filter that removes the blue color from the support icon
    // https://angel-rs.github.io/css-color-filter-generator/?color=00D64F
    faqContactIconFilter: isCash
      ? "brightness(0) saturate(100%) invert(67%) sepia(50%) saturate(5121%) hue-rotate(102deg) brightness(102%) contrast(103%);"
      : undefined,
  };

  return palette;
};

export const useNormalizedBrandPaletteValues = (
  theme: Theme
): Record<string, string | undefined> => {
  const cacheKey = JSON.stringify(theme);

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const palette = calculateNormalizedBrandPaletteValues(theme);
  cache.set(cacheKey, palette);

  return palette;
};
